import React, { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { RegisterData } from '../types'

interface RegisterFormProps {
  onClose: () => void
  onSwitchToLogin: () => void
}

export default function RegisterForm({ onClose, onSwitchToLogin }: RegisterFormProps) {
  const { register, isLoading } = useAuth()
  const [formData, setFormData] = useState<RegisterData>({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    dateOfBirth: '',
    agreeToTerms: false,
    ageVerification: false,
    productInterests: [],
    howDidYouHear: '',
    referralCode: '',
    currentShop: '',
    profilePicture: '',
    inviteCode: ''
  })
  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {}

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required'
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters'
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    if (!formData.password) {
      newErrors.password = 'Password is required'
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters'
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }

    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of birth is required'
    } else {
      const birthDate = new Date(formData.dateOfBirth)
      const age = new Date().getFullYear() - birthDate.getFullYear()
      if (age < 21) {
        newErrors.dateOfBirth = 'You must be at least 21 years old to register'
      }
    }

    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms of service'
    }

    if (!formData.ageVerification) {
      newErrors.ageVerification = 'You must confirm you are 21 or older'
    }

    if (formData.productInterests.length === 0) {
      newErrors.productInterests = 'Please select at least one product interest'
    }

    if (!formData.howDidYouHear.trim()) {
      newErrors.howDidYouHear = 'Please tell us how you heard about us'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    const success = await register(formData)
    if (success) {
      onClose()
    } else {
      setErrors({ submit: 'Registration failed. Username or email may already be taken.' })
    }
  }

  return (
    <div className="auth-form-overlay">
      <div className="auth-form">
        <div className="auth-form-header">
          <h2>Create Account</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="auth-form-content">
          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              className={errors.username ? 'error' : ''}
              placeholder="Choose a username"
            />
            {errors.username && <span className="error-message">{errors.username}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={errors.email ? 'error' : ''}
              placeholder="your@email.com"
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className={errors.password ? 'error' : ''}
              placeholder="At least 6 characters"
            />
            {errors.password && <span className="error-message">{errors.password}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className={errors.confirmPassword ? 'error' : ''}
              placeholder="Re-enter your password"
            />
            {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="dateOfBirth">Date of Birth</label>
            <input
              type="date"
              id="dateOfBirth"
              name="dateOfBirth"
              value={formData.dateOfBirth}
              onChange={handleInputChange}
              className={errors.dateOfBirth ? 'error' : ''}
              max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
            />
            {errors.dateOfBirth && <span className="error-message">{errors.dateOfBirth}</span>}
          </div>

          <div className="form-group">
            <label>Product Interests (select at least one)</label>
            <div className="checkbox-grid">
              {['7OH', 'Kratom', 'MIT', 'MGM', 'Kava'].map((product) => (
                <label key={product} className="checkbox-label">
                  <input
                    type="checkbox"
                    value={product}
                    checked={formData.productInterests.includes(product)}
                    onChange={(e) => {
                      const { value, checked } = e.target
                      setFormData(prev => ({
                        ...prev,
                        productInterests: checked 
                          ? [...prev.productInterests, value]
                          : prev.productInterests.filter(p => p !== value)
                      }))
                      if (errors.productInterests) {
                        setErrors(prev => ({ ...prev, productInterests: '' }))
                      }
                    }}
                  />
                  <span>{product}</span>
                </label>
              ))}
            </div>
            {errors.productInterests && <span className="error-message">{errors.productInterests}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="howDidYouHear">How did you hear about us?</label>
            <select
              id="howDidYouHear"
              name="howDidYouHear"
              value={formData.howDidYouHear}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, howDidYouHear: e.target.value }))
                if (errors.howDidYouHear) {
                  setErrors(prev => ({ ...prev, howDidYouHear: '' }))
                }
              }}
              className={errors.howDidYouHear ? 'error' : ''}
            >
              <option value="">Select an option</option>
              <option value="search">Google/Search Engine</option>
              <option value="social">Social Media</option>
              <option value="reddit">Reddit</option>
              <option value="discord">Discord</option>
              <option value="friend">Friend/Word of Mouth</option>
              <option value="vendor">From a Vendor</option>
              <option value="forum">Other Forum/Website</option>
              <option value="other">Other</option>
            </select>
            {errors.howDidYouHear && <span className="error-message">{errors.howDidYouHear}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="referralCode">Referral Code (optional)</label>
            <input
              type="text"
              id="referralCode"
              name="referralCode"
              value={formData.referralCode}
              onChange={handleInputChange}
              placeholder="Enter referral code if you have one"
            />
          </div>

          <div className="form-group">
            <label htmlFor="currentShop">Where do you currently shop? (optional)</label>
            <input
              type="text"
              id="currentShop"
              name="currentShop"
              value={formData.currentShop}
              onChange={handleInputChange}
              placeholder="Current vendor or shop name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="profilePicture">Profile Picture (optional)</label>
            <input
              type="file"
              id="profilePicture"
              name="profilePicture"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) {
                  // Convert to base64 for demo purposes
                  const reader = new FileReader()
                  reader.onload = (event) => {
                    setFormData(prev => ({ 
                      ...prev, 
                      profilePicture: event.target?.result as string 
                    }))
                  }
                  reader.readAsDataURL(file)
                }
              }}
              className="file-input"
            />
            <div className="file-input-hint">
              Accepted formats: JPG, PNG, GIF. Max size: 2MB
            </div>
          </div>

          <div className="checkbox-section">
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="ageVerification"
                  checked={formData.ageVerification}
                  onChange={handleInputChange}
                />
                <span>I confirm that I am 21 years of age or older</span>
              </label>
              {errors.ageVerification && <span className="error-message">{errors.ageVerification}</span>}
            </div>

            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="agreeToTerms"
                  checked={formData.agreeToTerms}
                  onChange={handleInputChange}
                />
                <span>I agree to the Terms of Service and Privacy Policy</span>
              </label>
              {errors.agreeToTerms && <span className="error-message">{errors.agreeToTerms}</span>}
            </div>
          </div>

          {errors.submit && (
            <div className="error-message submit-error">{errors.submit}</div>
          )}

          <div className="auth-form-actions">
            <button
              type="submit"
              className="submit-btn"
              disabled={isLoading}
            >
              {isLoading ? 'Creating Account...' : 'Create Account'}
            </button>
            
            <button
              type="button"
              className="switch-btn"
              onClick={onSwitchToLogin}
            >
              Already have an account? Log in
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}